import { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, FileText, Save, Loader2, GripVertical, Clock, CheckCircle, XCircle, Send, ChevronDown, ChevronRight, Paperclip, Lock, Shield, Code, Search, Globe, PenTool } from 'lucide-react';
import { NmiSettings } from '../components/NmiSettings';
import { ESignSettings } from '../components/ESignSettings';
import { MultisiteTemplateSettings } from '../components/MultisiteTemplateSettings';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  var authCtx = useAuth();
  var token = authCtx.token;

  var [expandedLog, setExpandedLog] = useState(null);
  var [mailMethod, setMailMethod] = useState('smtp');
  var [smtpHost, setSmtpHost] = useState('');
  var [smtpPort, setSmtpPort] = useState(587);
  var [smtpUser, setSmtpUser] = useState('');
  var [smtpPass, setSmtpPass] = useState('');
  var [smtpFrom, setSmtpFrom] = useState('');
  var [smtpName, setSmtpName] = useState('Next Business Energy');
  var [smtpTls, setSmtpTls] = useState(true);
  var [smtpSaving, setSmtpSaving] = useState(false);
  var [smtpTesting, setSmtpTesting] = useState(false);
  var [mailgunApiKey, setMailgunApiKey] = useState('');
  var [mailgunDomain, setMailgunDomain] = useState('');
  var [mailgunRegion, setMailgunRegion] = useState('us');

  var [emailLogs, setEmailLogs] = useState([]);
  var [emailLogsTotal, setEmailLogsTotal] = useState(0);
  var [emailLogsLoading, setEmailLogsLoading] = useState(false);

  var [companyName, setCompanyName] = useState('Next Business Energy');
  var [companyAbn, setCompanyAbn] = useState('');
  var [companyPhone, setCompanyPhone] = useState('');
  var [companyWeb, setCompanyWeb] = useState('');
  var [companyEmail, setCompanyEmail] = useState('');
  var [accentColor, setAccentColor] = useState('#1A7AB8');
  var [logoUrl, setLogoUrl] = useState('');
  var [headerText, setHeaderText] = useState('');
  var [footerText, setFooterText] = useState('This comparison is an estimate based on the information provided and is valid for 30 days.');
  var [sections, setSections] = useState([
    { id: 'customer_info', label: 'Customer Information', enabled: true },
    { id: 'cost_comparison', label: 'Cost Comparison Table', enabled: true },
    { id: 'retailer_breakdown', label: 'Current Retailer Breakdown', enabled: true },
    { id: 'nbe_breakdown', label: 'NBE Offer Breakdown', enabled: true },
    { id: 'assumptions', label: 'Assumptions & Notes', enabled: true },
  ]);
  var [templateSaving, setTemplateSaving] = useState(false);
  var [dragIdx, setDragIdx] = useState(null);

  function fetchEmailLogs() {
    setEmailLogsLoading(true);
    axios.get(API_URL + '/api/email-logs?authorization=' + token).then(function(r) {
      setEmailLogs(r.data.logs || []);
      setEmailLogsTotal(r.data.total || 0);
    }).catch(function() {}).finally(function() { setEmailLogsLoading(false); });
  }

  useEffect(function() {
    axios.get(API_URL + '/api/settings/smtp?authorization=' + token).then(function(r) {
      var d = r.data;
      if (d.mail_method) setMailMethod(d.mail_method);
      if (d.host) setSmtpHost(d.host);
      if (d.port) setSmtpPort(d.port);
      if (d.username) setSmtpUser(d.username);
      if (d.from_email) setSmtpFrom(d.from_email);
      if (d.from_name) setSmtpName(d.from_name);
      if (d.use_tls !== undefined) setSmtpTls(d.use_tls);
      if (d.mailgun_domain) setMailgunDomain(d.mailgun_domain);
      if (d.mailgun_region) setMailgunRegion(d.mailgun_region);
    }).catch(function() {});
    axios.get(API_URL + '/api/settings/pdf-template?authorization=' + token).then(function(r) {
      var d = r.data;
      if (d.company_name) setCompanyName(d.company_name);
      if (d.company_abn) setCompanyAbn(d.company_abn);
      if (d.company_phone) setCompanyPhone(d.company_phone);
      if (d.company_website) setCompanyWeb(d.company_website);
      if (d.company_email) setCompanyEmail(d.company_email);
      if (d.accent_color) setAccentColor(d.accent_color);
      if (d.logo_url) setLogoUrl(d.logo_url);
      if (d.header_text !== undefined) setHeaderText(d.header_text);
      if (d.footer_text !== undefined) setFooterText(d.footer_text);
      if (d.sections) setSections(d.sections);
    }).catch(function() {});
    fetchEmailLogs();
  }, [token]);

  function saveSmtp() {
    setSmtpSaving(true);
    var form = new FormData();
    form.append('mail_method', mailMethod);
    form.append('host', smtpHost);
    form.append('port', smtpPort);
    form.append('username', smtpUser);
    form.append('password', smtpPass);
    form.append('from_email', smtpFrom);
    form.append('from_name', smtpName);
    form.append('use_tls', smtpTls);
    form.append('mailgun_api_key', mailgunApiKey);
    form.append('mailgun_domain', mailgunDomain);
    form.append('mailgun_region', mailgunRegion);
    axios.post(API_URL + '/api/settings/smtp?authorization=' + token, form)
      .then(function() { toast.success('Email settings saved'); setSmtpPass(''); setMailgunApiKey(''); })
      .catch(function(err) { toast.error('Failed to save'); })
      .finally(function() { setSmtpSaving(false); });
  }

  function testSmtp() {
    setSmtpTesting(true);
    axios.post(API_URL + '/api/settings/smtp/test?authorization=' + token)
      .then(function(res) { toast.success(res.data.message); })
      .catch(function(err) { toast.error('SMTP test failed'); })
      .finally(function() { setSmtpTesting(false); });
  }

  function saveTemplate() {
    setTemplateSaving(true);
    var data = {
      company_name: companyName, company_abn: companyAbn, company_phone: companyPhone,
      company_website: companyWeb, company_email: companyEmail, accent_color: accentColor,
      logo_url: logoUrl, header_text: headerText, footer_text: footerText, sections: sections
    };
    var form = new FormData();
    form.append('template', JSON.stringify(data));
    axios.post(API_URL + '/api/settings/pdf-template?authorization=' + token, form)
      .then(function() { toast.success('PDF template saved'); })
      .catch(function() { toast.error('Failed to save template'); })
      .finally(function() { setTemplateSaving(false); });
  }

  // Portal gate password
  var [portalGatePassword, setPortalGatePassword] = useState('');
  var [portalGateSaving, setPortalGateSaving] = useState(false);
  var [publicToolEnabled, setPublicToolEnabled] = useState(true);
  var [publicToolSaving, setPublicToolSaving] = useState(false);

  // HTML PDF Template
  var [htmlTpl, setHtmlTpl] = useState('');
  var [htmlTplEnabled, setHtmlTplEnabled] = useState(false);
  var [htmlTplSaving, setHtmlTplSaving] = useState(false);
  var [htmlTplPreview, setHtmlTplPreview] = useState(false);
  var iframeRef = useRef(null);

  useEffect(function() {
    axios.get(API_URL + '/api/settings/portal-gate?authorization=' + token).then(function(r) {
      setPortalGatePassword(r.data.password || '');
    }).catch(function() {});
    axios.get(API_URL + '/api/portal/status').then(function(r) {
      setPublicToolEnabled(r.data.enabled !== false);
    }).catch(function() {});
    axios.get(API_URL + '/api/settings/html-pdf-template?authorization=' + token).then(function(r) {
      setHtmlTpl(r.data.html_template || '');
      setHtmlTplEnabled(r.data.enabled || false);
    }).catch(function() {});
  }, [token]);

  function saveHtmlTpl() {
    setHtmlTplSaving(true);
    var form = new FormData();
    form.append('html_template', htmlTpl);
    form.append('enabled', htmlTplEnabled ? 'true' : 'false');
    axios.post(API_URL + '/api/settings/html-pdf-template?authorization=' + token, form)
      .then(function() { toast.success('HTML PDF template saved'); })
      .catch(function() { toast.error('Failed to save'); })
      .finally(function() { setHtmlTplSaving(false); });
  }

  function savePortalGate() {
    setPortalGateSaving(true);
    axios.post(API_URL + '/api/settings/portal-gate?authorization=' + token, { password: portalGatePassword })
      .then(function(r) { toast.success(r.data.message); })
      .catch(function() { toast.error('Failed to save'); })
      .finally(function() { setPortalGateSaving(false); });
  }

  function togglePublicTool() {
    var newVal = !publicToolEnabled;
    setPublicToolSaving(true);
    axios.post(API_URL + '/api/settings/public-tool?authorization=' + token, { enabled: newVal })
      .then(function() { setPublicToolEnabled(newVal); toast.success(newVal ? 'Public comparison tool enabled' : 'Public comparison tool disabled'); })
      .catch(function() { toast.error('Failed to update'); })
      .finally(function() { setPublicToolSaving(false); });
  }

  function toggleSection(idx, val) {
    var s = sections.slice();
    s[idx] = { id: s[idx].id, label: s[idx].label, enabled: val };
    setSections(s);
  }

  function moveSectionDrag(fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    var s = sections.slice();
    var item = s.splice(fromIdx, 1)[0];
    s.splice(toIdx, 0, item);
    setSections(s);
    setDragIdx(toIdx);
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>

        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="email"><Mail className="w-4 h-4 mr-2" />Email</TabsTrigger>
            <TabsTrigger value="email-log" onClick={fetchEmailLogs}><Send className="w-4 h-4 mr-2" />Email Log</TabsTrigger>
            <TabsTrigger value="html-template"><Code className="w-4 h-4 mr-2" />PDF Template</TabsTrigger>
            <TabsTrigger value="nmi-lookup"><Search className="w-4 h-4 mr-2" />NMI Lookup</TabsTrigger>
            <TabsTrigger value="docusign" data-testid="settings-docusign-tab"><PenTool className="w-4 h-4 mr-2" />eSignature</TabsTrigger>
            <TabsTrigger value="portal-access"><Lock className="w-4 h-4 mr-2" />Portal Access</TabsTrigger>
          </TabsList>

          <TabsContent value="docusign">
            <ESignSettings />
          </TabsContent>

          <TabsContent value="email">
            <Card>
              <CardHeader><CardTitle>Email Configuration</CardTitle><CardDescription>Configure how emails are sent to customers. Choose your preferred sending method.</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold">Sending Method</Label>
                  <div className="flex gap-2 mt-2">
                    <Button variant={mailMethod === 'smtp' ? 'default' : 'outline'} size="sm" onClick={function() { setMailMethod('smtp'); }} data-testid="mail-method-smtp">SMTP</Button>
                    <Button variant={mailMethod === 'mailgun_api' ? 'default' : 'outline'} size="sm" onClick={function() { setMailMethod('mailgun_api'); }} data-testid="mail-method-mailgun">Mailgun API</Button>
                    <Button variant={mailMethod === 'phpmail' ? 'default' : 'outline'} size="sm" onClick={function() { setMailMethod('phpmail'); }} data-testid="mail-method-phpmail">Sendmail</Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{mailMethod === 'smtp' ? 'Uses external SMTP server (Gmail, Outlook, SendGrid, Mailgun, etc.)' : mailMethod === 'mailgun_api' ? 'Uses Mailgun HTTP API — more reliable than SMTP in some environments' : 'Uses system sendmail/postfix — no external service required'}</p>
                </div>

                {mailMethod === 'smtp' && (
                  <>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1">Quick Presets</Label>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { label: 'Mailgun', host: 'smtp.mailgun.org', port: 587, tls: true },
                          { label: 'Gmail', host: 'smtp.gmail.com', port: 587, tls: true },
                          { label: 'Outlook', host: 'smtp.office365.com', port: 587, tls: true },
                          { label: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, tls: true },
                        ].map(function(p) {
                          return <Button key={p.label} variant="ghost" size="sm" className="text-xs h-7 border border-slate-200" onClick={function() { setSmtpHost(p.host); setSmtpPort(p.port); setSmtpTls(p.tls); }} data-testid={'smtp-preset-' + p.label.toLowerCase()}>{p.label}</Button>;
                        })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>SMTP Host *</Label><Input value={smtpHost} onChange={function(e) { setSmtpHost(e.target.value); }} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
                      <div><Label>Port</Label><Input type="number" value={smtpPort} onChange={function(e) { setSmtpPort(parseInt(e.target.value) || 587); }} data-testid="smtp-port" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Username</Label><Input value={smtpUser} onChange={function(e) { setSmtpUser(e.target.value); }} placeholder="user@example.com" data-testid="smtp-username" /></div>
                      <div><Label>Password</Label><Input type="password" value={smtpPass} onChange={function(e) { setSmtpPass(e.target.value); }} placeholder="Enter to update" data-testid="smtp-password" /></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={smtpTls} onCheckedChange={setSmtpTls} className="data-[state=unchecked]:bg-slate-300" />
                      <Label>Use TLS</Label>
                    </div>
                  </>
                )}

                {mailMethod === 'mailgun_api' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Mailgun Domain *</Label><Input value={mailgunDomain} onChange={function(e) { setMailgunDomain(e.target.value); }} placeholder="nbecomparison.com" data-testid="mailgun-domain" /></div>
                      <div>
                        <Label>Region</Label>
                        <div className="flex gap-2 mt-1">
                          <Button variant={mailgunRegion === 'us' ? 'default' : 'outline'} size="sm" onClick={function() { setMailgunRegion('us'); }} data-testid="mailgun-region-us">US</Button>
                          <Button variant={mailgunRegion === 'eu' ? 'default' : 'outline'} size="sm" onClick={function() { setMailgunRegion('eu'); }} data-testid="mailgun-region-eu">EU</Button>
                        </div>
                      </div>
                    </div>
                    <div><Label>API Key *</Label><Input type="password" value={mailgunApiKey} onChange={function(e) { setMailgunApiKey(e.target.value); }} placeholder="Enter Mailgun API key" data-testid="mailgun-api-key" /></div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div><Label>From Email *</Label><Input value={smtpFrom} onChange={function(e) { setSmtpFrom(e.target.value); }} placeholder="noreply@nbe.com.au" data-testid="smtp-from-email" /></div>
                  <div><Label>From Name</Label><Input value={smtpName} onChange={function(e) { setSmtpName(e.target.value); }} data-testid="smtp-from-name" /></div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={saveSmtp} disabled={smtpSaving} className="bg-primary hover:bg-primary/90" data-testid="save-smtp-btn">
                    {smtpSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Settings
                  </Button>
                  <Button variant="outline" onClick={testSmtp} disabled={smtpTesting || (mailMethod === 'smtp' && !smtpHost) || (mailMethod === 'mailgun_api' && !mailgunDomain)} data-testid="test-smtp-btn">
                    {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Send Test Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email-log">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div><CardTitle>Email Log</CardTitle><CardDescription>History of all emails sent to customers</CardDescription></div>
                  <Button variant="outline" size="sm" onClick={fetchEmailLogs} disabled={emailLogsLoading} data-testid="refresh-email-log">{emailLogsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}</Button>
                </div>
              </CardHeader>
              <CardContent>
                {emailLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="email-log-table">
                      <thead>
                        <tr className="bg-slate-100 text-slate-600 text-xs">
                          <th className="p-2 text-left font-medium w-8"></th>
                          <th className="p-2 text-left font-medium">Date</th>
                          <th className="p-2 text-left font-medium">Recipient</th>
                          <th className="p-2 text-left font-medium">Subject</th>
                          <th className="p-2 text-left font-medium">Report ID</th>
                          <th className="p-2 text-left font-medium">Type</th>
                          <th className="p-2 text-left font-medium">Sent By</th>
                          <th className="p-2 text-left font-medium">Method</th>
                          <th className="p-2 text-left font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map(function(log) {
                          var isExpanded = expandedLog === log.id;
                          return (
                            <Fragment key={log.id}>
                              <tr className={'border-b hover:bg-slate-50 cursor-pointer' + (isExpanded ? ' bg-blue-50' : '')} onClick={function() { setExpandedLog(isExpanded ? null : log.id); }} data-testid={'email-log-row-' + log.id}>
                                <td className="p-2 text-xs text-slate-400">{isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                                <td className="p-2 text-xs text-slate-600">{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
                                <td className="p-2 text-xs"><div>{log.customer_name || '-'}</div><div className="text-slate-500">{log.customer_email}</div></td>
                                <td className="p-2 text-xs max-w-[200px] truncate">{log.subject || '-'}</td>
                                <td className="p-2 text-xs font-mono">{log.report_id || '-'}</td>
                                <td className="p-2 text-xs"><Badge variant="outline" className={'text-xs ' + (log.comparison_id && log.comparison_id.startsWith('renewal:') ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700')}>{log.comparison_id && log.comparison_id.startsWith('renewal:') ? 'Renewal' : 'Comparison'}</Badge></td>
                                <td className="p-2 text-xs">{log.sent_by_name || '-'}</td>
                                <td className="p-2 text-xs"><Badge variant="outline" className="text-xs">{log.mail_method === 'mailgun_api' ? 'Mailgun API' : log.mail_method === 'phpmail' || log.mail_method === 'sendmail' ? 'Sendmail' : 'SMTP'}</Badge></td>
                                <td className="p-2 text-xs">
                                  {log.status === 'sent' ? <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle className="w-3 h-3 mr-1 inline" />Sent</Badge> : <Badge className="bg-red-100 text-red-800 text-xs"><XCircle className="w-3 h-3 mr-1 inline" />Failed</Badge>}
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr data-testid={'email-log-detail-' + log.id}>
                                  <td colSpan="9" className="p-0">
                                    <div className="border-t border-blue-200 bg-blue-50/50 p-4 space-y-3">
                                      {log.has_attachment && (
                                        <div className="flex items-center gap-2 text-sm">
                                          <Paperclip className="w-4 h-4 text-slate-500" />
                                          <span className="font-medium text-slate-700">{log.attachment_name || 'PDF Attachment'}</span>
                                          <Badge variant="outline" className="text-xs">PDF</Badge>
                                        </div>
                                      )}
                                      {log.error && (
                                        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                                          <span className="font-medium">Error: </span>{log.error}
                                        </div>
                                      )}
                                      {log.email_html ? (
                                        <div>
                                          <p className="text-xs font-medium text-slate-600 mb-2">Email Content:</p>
                                          <div className="bg-white border rounded-lg shadow-inner overflow-hidden" style={{maxHeight: '400px', overflowY: 'auto'}}>
                                            <iframe title="Email Preview" srcDoc={log.email_html} className="w-full border-0" style={{minHeight: '300px', height: '100%'}} sandbox="" data-testid={'email-preview-' + log.id} />
                                          </div>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-500 italic">Email content not available for this log entry</p>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No emails sent yet</p>
                    <p className="text-xs text-slate-400 mt-1">Emails will appear here when you send comparisons to customers</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="html-template">
            <div className="space-y-4" data-testid="html-template-tab">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Custom HTML PDF Template</h2>
                  <p className="text-sm text-slate-500">When enabled, this HTML template overrides the visual template for all PDF exports. Use placeholders like {'{{customer_name}}'}, {'{{savings_amount}}'}, etc.</p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <span className="text-sm font-medium">Enable</span>
                    <input type="checkbox" checked={htmlTplEnabled} onChange={function(e) { setHtmlTplEnabled(e.target.checked); }} className="w-5 h-5" data-testid="html-template-toggle" />
                  </label>
                  <button onClick={function() { setHtmlTplPreview(!htmlTplPreview); }} className="px-3 py-2 text-sm border rounded-lg hover:bg-slate-50" data-testid="html-preview-toggle">{htmlTplPreview ? 'Code' : 'Preview'}</button>
                  <button onClick={saveHtmlTpl} disabled={htmlTplSaving} className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50" data-testid="save-html-template-btn">{htmlTplSaving ? 'Saving...' : 'Save'}</button>
                </div>
              </div>
              {htmlTplPreview ? (
                <iframe ref={iframeRef} title="Preview" srcDoc={htmlTpl} className="w-full bg-white border rounded-lg" style={{ height: 600 }} sandbox="" data-testid="html-template-preview" />
              ) : (
                <textarea value={htmlTpl} onChange={function(e) { setHtmlTpl(e.target.value); }} className="w-full font-mono text-xs border rounded-lg p-3 bg-slate-950 text-green-400 focus:outline-none focus:ring-2 focus:ring-primary/50" style={{ height: 600, tabSize: 2, resize: 'vertical' }} spellCheck={false} placeholder="Paste or write your HTML template here. Use placeholders like {{customer_name}}, {{savings_amount}}, {{retailer_total}}, {{our_total}}, etc." data-testid="html-template-editor" />
              )}
              <div className="p-4 bg-white rounded-lg border text-xs text-slate-600">
                <p className="font-semibold text-sm text-slate-900 mb-2">Available Placeholders:</p>
                <p><strong>Customer:</strong> {'{{customer_name}}'} {'{{nmi}}'} {'{{site_address}}'} {'{{retailer_name}}'} {'{{billing_period_start}}'} {'{{billing_period_end}}'} {'{{total_kwh}}'} {'{{supply_days}}'}</p>
                <p><strong>Report:</strong> {'{{report_id}}'} {'{{report_date}}'} {'{{prepared_by}}'} {'{{plan_name}}'} {'{{logo_url}}'} {'{{company_name}}'} {'{{company_abn}}'} {'{{company_phone}}'} {'{{company_website}}'} {'{{company_email}}'} {'{{footer_text}}'}</p>
                <p><strong>Costs:</strong> {'{{retailer_supply_cost}}'} {'{{retailer_usage_cost}}'} {'{{retailer_demand_cost}}'} {'{{retailer_discount}}'} {'{{retailer_solar_credit}}'} {'{{retailer_total}}'} {'{{our_supply_cost}}'} {'{{our_usage_cost}}'} {'{{our_demand_cost}}'} {'{{our_discount}}'} {'{{our_solar_credit}}'} {'{{our_greenpower_cost}}'} {'{{our_metering_cost}}'} {'{{our_total}}'}</p>
                <p><strong>Savings:</strong> {'{{savings_amount}}'} {'{{savings_percentage}}'} {'{{annual_savings}}'} {'{{savings_direction}}'} {'{{savings_label}}'} {'{{savings_bg_color}}'} {'{{savings_border_color}}'} {'{{savings_text_color}}'}</p>
                <p><strong>Dynamic:</strong> {'{{solar_row}}'} {'{{greenpower_row}}'} {'{{metering_row}}'} {'{{assumptions_list}}'} {'{{retailer_breakdown_rows}}'} {'{{nbe_breakdown_rows}}'}</p>
              </div>
              <MultisiteTemplateSettings />
            </div>
          </TabsContent>

          <TabsContent value="nmi-lookup">
            <NmiSettings token={token} />
          </TabsContent>

          <TabsContent value="portal-access">
            <Card className="mb-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={'w-10 h-10 rounded-full flex items-center justify-center ' + (publicToolEnabled ? 'bg-emerald-100' : 'bg-red-100')}><Globe className={'w-5 h-5 ' + (publicToolEnabled ? 'text-emerald-600' : 'text-red-500')} /></div>
                    <div>
                      <CardTitle>Public Comparison Tool</CardTitle>
                      <CardDescription>Control access to the customer-facing bill comparison page</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={'text-sm font-medium ' + (publicToolEnabled ? 'text-emerald-600' : 'text-red-500')}>{publicToolEnabled ? 'Live' : 'Offline'}</span>
                    <Switch checked={publicToolEnabled} onCheckedChange={togglePublicTool} disabled={publicToolSaving} className="data-[state=unchecked]:bg-red-300" data-testid="public-tool-toggle" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500">{publicToolEnabled ? 'The public comparison tool is currently live. Customers can upload bills and get comparisons.' : 'The public comparison tool is offline. Customers will see a maintenance message.'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><Shield className="w-5 h-5 text-primary" /></div>
                  <div><CardTitle>Portal Access Password</CardTitle><CardDescription>Require a password before users can see the login/register page</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Access Password</Label>
                  <Input value={portalGatePassword} onChange={function(e) { setPortalGatePassword(e.target.value); }} placeholder="Enter a gate password (leave blank to disable)" className="mt-1" data-testid="portal-gate-password-input" />
                  <p className="text-xs text-slate-500 mt-1">{portalGatePassword ? 'Gate is active — visitors must enter this password to access login/register' : 'Gate is disabled — anyone can access login/register directly'}</p>
                </div>
                <Button onClick={savePortalGate} disabled={portalGateSaving} className="bg-primary hover:bg-primary/90" data-testid="save-portal-gate-btn">
                  {portalGateSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
